import TenantBrandingSettings from '../components/TenantBrandingSettings';
import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { KeyIcon, EyeIcon, EyeSlashIcon, SparklesIcon, MapPinIcon, CubeIcon, PaperClipIcon, CircleStackIcon, HashtagIcon } from '@heroicons/react/24/outline';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, wmsAPI, storageConfigAPI, productsAPI, tenantsAPI, offersAPI, aiAPI, OfferNumberingSettings } from '../utils/api';
import { WmsConnectionStatus, WmsMapping, Prodotto, StorageConfigStatus, StorageProvider } from '../types';
import { usePermissions } from '../hooks/usePermissions';
import toast from 'react-hot-toast';

// AI Configuration Types
interface AIConfig {
  id: string;
  tenant_id: string;
  provider: 'openai' | 'anthropic' | 'ollama' | 'openrouter';
  model: string;
  has_api_key: boolean;
  api_endpoint: string | null;
  fallback_provider: 'openai' | 'anthropic' | 'ollama' | 'openrouter' | null;
  fallback_model: string | null;
  has_fallback_api_key: boolean;
  fallback_api_endpoint: string | null;
  ai_enabled: boolean;
  ai_suggestions_enabled: boolean;
  ai_generation_enabled: boolean;
  ai_improvement_enabled: boolean;
  max_tokens_per_request: number;
  max_requests_per_hour: number;
  max_tokens_per_month: number;
  current_month_tokens: number;
  current_month: string | null;
  temperature: number;
  max_output_tokens: number;
  has_custom_prompts: boolean;
  created_at: string;
  updated_at: string;
}

interface AIStatus {
  ai_available: boolean;
  ai_configured: boolean;
  ai_enabled: boolean;
  provider: string | null;
  model: string | null;
  features: {
    suggestions: boolean;
    generation: boolean;
    improvement: boolean;
  } | null;
}

interface AIUsageStats {
  requests_this_hour: number;
  tokens_this_month: number;
  hour: string;
  month: string;
  limits: {
    max_requests_per_hour: number;
    max_tokens_per_month: number;
  } | null;
  ai_enabled: boolean | null;
  provider: string | null;
  tokens_remaining_this_month: number | null;
  requests_remaining_this_hour: number | null;
}

interface AIUsageHistoryMonth {
  month: string;
  total_tokens: number;
  requests: number;
  success_count: number;
  operations: Record<string, number>;
}

const PROVIDER_LABELS: Record<string, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic (Claude)',
  ollama: 'Ollama (Locale)',
  openrouter: 'OpenRouter',
};

import HostSettings from './HostSettings';

type SettingsTab = 'branding' | 'integrazione' | 'assistente' | 'geocodifica' | 'numerazione' | 'magazzino' | 'storage' | 'allegati';

const TABS: { key: SettingsTab; label: string; icon: React.ElementType; adminOnly?: boolean; permission?: string }[] = [
  { key: 'integrazione', label: 'Integrazione', icon: KeyIcon, permission: 'settings:integrations' },
  { key: 'assistente', label: 'Assistente', icon: SparklesIcon, permission: 'settings:integrations' },
  { key: 'geocodifica', label: 'Geocodifica', icon: MapPinIcon, permission: 'settings:integrations' },
  { key: 'magazzino', label: 'Magazzino', icon: CubeIcon, permission: 'settings:integrations' },
  { key: 'numerazione', label: 'Numerazione offerte', icon: HashtagIcon, permission: 'settings:integrations' },
  { key: 'branding', label: 'Branding', icon: SparklesIcon, adminOnly: true },
  { key: 'storage', label: 'Storage', icon: CircleStackIcon, adminOnly: true },
  { key: 'allegati', label: 'Allegati Promozioni', icon: PaperClipIcon, adminOnly: true },
];

const NUMBERING_PATTERN_MAX_LENGTH = 50;

// Renderizza il pattern di numerazione lato client con lo stesso algoritmo del backend:
// YYYY → anno a 4 cifre, YY → anno a 2 cifre, gruppi di N → progressivo con zero-padding.
const renderNumberingPattern = (pattern: string, anno: number, seq: number): string => {
  let result = '';
  let i = 0;
  while (i < pattern.length) {
    if (pattern.startsWith('YYYY', i)) {
      result += String(anno);
      i += 4;
    } else if (pattern.startsWith('YY', i)) {
      result += String(anno % 100).padStart(2, '0');
      i += 2;
    } else if (pattern[i] === 'N') {
      let j = i;
      while (j < pattern.length && pattern[j] === 'N') j++;
      result += String(seq).padStart(j - i, '0');
      i = j;
    } else {
      result += pattern[i];
      i++;
    }
  }
  return result;
};

const STORAGE_PROVIDER_PRESETS: Record<StorageProvider, { label: string; endpoint: string; region: string }> = {
  ovh: { label: 'OVH Object Storage', endpoint: 'https://s3.gra.io.cloud.ovh.net', region: 'gra' },
  aws: { label: 'Amazon S3', endpoint: 'https://s3.amazonaws.com', region: 'us-east-1' },
  gcs: { label: 'Google Cloud Storage', endpoint: 'https://storage.googleapis.com', region: 'auto' },
};

const ALL_KNOWN_FORMATS = [
  'pdf', 'doc', 'docx', 'xls', 'xlsx', 'png', 'jpg', 'jpeg', 'csv', 'txt',
] as const;

const FORMAT_DESCRIPTIONS: Record<string, string> = {
  pdf: 'Documenti PDF',
  doc: 'Word (legacy)',
  docx: 'Word',
  xls: 'Excel (legacy)',
  xlsx: 'Excel',
  png: 'Immagine PNG',
  jpg: 'Immagine JPG',
  jpeg: 'Immagine JPEG',
  csv: 'CSV',
  txt: 'Testo semplice',
};

const TenantSettings: React.FC = () => {
  const { user, tenant } = useAuth();
  const { isAdmin, hasPermission } = usePermissions();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<SettingsTab>('integrazione');

  const visibleTabs = useMemo(
    () => TABS.filter((t) => (!t.adminOnly || isAdmin) && (!t.permission || hasPermission(t.permission))),
    [isAdmin, hasPermission]
  );

  // Ensure the active tab is always one the current user can actually see,
  // otherwise the page can render blank (e.g. 'integrazione' hidden for the user).
  useEffect(() => {
    if (visibleTabs.length === 0) return;
    if (!visibleTabs.some((t) => t.key === activeTab)) {
      setActiveTab(visibleTabs[0].key);
    }
  }, [visibleTabs, activeTab]);

  // Allegati Promozioni state
  const [allegatiFormats, setAllegatiFormats] = useState<string[]>([]);
  const [allegatiInitialFormats, setAllegatiInitialFormats] = useState<string[]>([]);

  // FIC credentials form state


  // Geocoding configuration state
  const [isEditingGeocoding, setIsEditingGeocoding] = useState(false);
  const [showGoogleKey, setShowGoogleKey] = useState(false);
  const [geocodingFormData, setGeocodingFormData] = useState({
    provider: 'nominatim' as 'nominatim' | 'google',
    nominatim_url: '',
    google_api_key: '',
  });

  // Numerazione offerte state
  const [numerazionePattern, setNumerazionePattern] = useState('');

  // ===== WMS Configuration State =====
  const [isEditingWms, setIsEditingWms] = useState(false);
  const [showWmsToken, setShowWmsToken] = useState(false);
  const [wmsFormData, setWmsFormData] = useState({
    base_url: '',
    api_token: '',
    auth_mode: 'api_token' as 'api_token' | 'client_credentials',
    oauth_authority: '',
    oauth_client_id: '',
    oauth_client_secret: '',
    oauth_tenant_id: '',
    default_warehouse_id: '',
    default_warehouse_name: '',
  });
  const [mappingPage, setMappingPage] = useState(1);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [materialSearch, setMaterialSearch] = useState('');
  const [selectedMaterial, setSelectedMaterial] = useState<{ id: string; code: string; name?: string } | null>(null);
  const [selectedProdotto, setSelectedProdotto] = useState('');

  // ===== Storage (S3) Configuration State =====
  const [isEditingStorage, setIsEditingStorage] = useState(false);
  const [showStorageSecret, setShowStorageSecret] = useState(false);
  const [storageFormData, setStorageFormData] = useState({
    provider: 'ovh' as StorageProvider,
    endpoint: '',
    public_endpoint: '',
    bucket: '',
    region: '',
    access_key: '',
    secret_key: '',
  });

  // Fetch WMS status
  const { data: wmsStatus, isLoading: wmsLoading } = useQuery<WmsConnectionStatus>({
    queryKey: ['wms-status'],
    queryFn: async () => {
      const response = await wmsAPI.getStatus();
      return response.data;
    },
    enabled: !!tenant && !user?.is_host && activeTab === 'magazzino',
  });

  // ===== Storage (S3) status + mutations =====
  const { data: storageDefaults } = useQuery<{ endpoint: string; region: string }>({
    queryKey: ['storage-defaults', tenant?.id],
    queryFn: async () => (await apiClient.client.get('/storage/defaults')).data,
    enabled: !!tenant && isAdmin && activeTab === 'storage',
  });

  const { data: storageStatus, isLoading: storageLoading } = useQuery<StorageConfigStatus>({
    queryKey: ['storage-config'],
    queryFn: async () => (await storageConfigAPI.getStatus()).data,
    enabled: !!tenant && !user?.is_host && isAdmin && activeTab === 'storage',
  });

  const saveStorageConfig = useMutation({
    mutationFn: async (data: any) =>
      storageStatus?.configured ? storageConfigAPI.updateConfig(data) : storageConfigAPI.saveConfig(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['storage-config'] });
      toast.success('Configurazione storage salvata');
      setIsEditingStorage(false);
    },
    onError: (error: any) => toast.error(error?.response?.data?.detail || 'Errore nel salvataggio storage'),
  });

  const testStorageConnection = useMutation({
    mutationFn: () => storageConfigAPI.testConnection(),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['storage-config'] });
      response.data.success
        ? toast.success(response.data.message || 'Connessione riuscita')
        : toast.error(response.data.message || 'Connessione fallita');
    },
    onError: (error: any) => toast.error(error?.response?.data?.detail || 'Test connessione fallito'),
  });

  const createStorageBucket = useMutation({
    mutationFn: () => apiClient.client.post('/storage/create-bucket'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['storage-config'] });
      toast.success('Bucket tenant pronto');
    },
    onError: () => toast.error('Creazione bucket non riuscita: verifica credenziali e permessi'),
  });

  const disconnectStorage = useMutation({
    mutationFn: () => storageConfigAPI.disconnect(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['storage-config'] });
      toast.success('Storage tenant disattivato');
    },
    onError: (error: any) => toast.error(error?.response?.data?.detail || 'Errore nella disconnessione'),
  });

  // Fetch WMS warehouses
  const { data: wmsWarehouses } = useQuery({
    queryKey: ['wms-warehouses'],
    queryFn: async () => {
      const response = await wmsAPI.getWarehouses();
      return response.data.data || [];
    },
    enabled: !!wmsStatus?.configured && isEditingWms,
  });

  // Fetch WMS mappings
  const { data: wmsMappingsData } = useQuery({
    queryKey: ['wms-mappings', mappingPage],
    queryFn: async () => {
      const response = await wmsAPI.getMappings({ page: mappingPage, limit: 20 });
      return response.data;
    },
    enabled: !!wmsStatus?.configured && activeTab === 'magazzino',
  });

  // Fetch products for mapping modal
  const { data: productsForMapping } = useQuery({
    queryKey: ['products-for-wms-mapping'],
    queryFn: async () => {
      const response = await productsAPI.getProductsForSelect(true);
      return response.data as Prodotto[];
    },
    enabled: showMappingModal,
  });

  // Search WMS materials
  const { data: wmsMaterialResults } = useQuery({
    queryKey: ['wms-materials-search', materialSearch],
    queryFn: async () => {
      const response = await wmsAPI.searchMaterials(materialSearch);
      return response.data.data || [];
    },
    enabled: materialSearch.length >= 2,
  });

  // WMS mutations
  const saveWmsConfig = useMutation({
    mutationFn: async (data: { base_url: string; api_token?: string; auth_mode?: 'api_token' | 'client_credentials'; oauth_authority?: string; oauth_client_id?: string; oauth_client_secret?: string; oauth_tenant_id?: string; default_warehouse_id?: string; default_warehouse_name?: string }) => {
      if (wmsStatus?.configured) {
        return wmsAPI.updateConfig(data);
      }
      return wmsAPI.saveConfig(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wms-status'] });
      toast.success('Configurazione WMS salvata');
      setWmsFormData((previous) => ({ ...previous, api_token: '', oauth_client_secret: '' }));
      setIsEditingWms(false);
    },
    onError: () => toast.error('Errore nel salvataggio configurazione WMS'),
  });

  const testWmsConnection = useMutation({
    mutationFn: () => wmsAPI.testConnection(),
    onSuccess: (response) => {
      toast.success(response.data.message || 'Connessione riuscita');
    },
    onError: () => toast.error('Test connessione fallito'),
  });

  const disconnectWms = useMutation({
    mutationFn: () => wmsAPI.disconnect(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wms-status'] });
      toast.success('WMS disconnesso');
    },
    onError: () => toast.error('Errore nella disconnessione'),
  });

  const wmsAutoMatch = useMutation({
    mutationFn: () => wmsAPI.autoMatch(),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['wms-mappings'] });
      const result = response.data;
      toast.success(`Auto-match: ${result.matched} abbinati, ${result.unmatched} non trovati`);
    },
    onError: () => toast.error('Errore nell\'auto-match'),
  });

  const createWmsMapping = useMutation({
    mutationFn: (data: { prodotto_id: string; wms_material_id: string; wms_material_code: string; wms_material_name?: string }) =>
      wmsAPI.createMapping(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wms-mappings'] });
      toast.success('Mapping creato');
      setShowMappingModal(false);
      setSelectedMaterial(null);
      setSelectedProdotto('');
      setMaterialSearch('');
    },
    onError: () => toast.error('Errore nella creazione del mapping'),
  });

  const deleteWmsMapping = useMutation({
    mutationFn: (id: string) => wmsAPI.deleteMapping(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wms-mappings'] });
      toast.success('Mapping eliminato');
    },
    onError: () => toast.error('Errore nell\'eliminazione'),
  });

  useEffect(() => {
    if (wmsStatus && isEditingWms) {
      setWmsFormData({
        auth_mode: wmsStatus.auth_mode || 'api_token',
        oauth_authority: wmsStatus.oauth_authority || '',
        oauth_client_id: wmsStatus.oauth_client_id || '',
        oauth_client_secret: '',
        oauth_tenant_id: wmsStatus.associated_wms_tenant_id || '',
        base_url: wmsStatus.base_url || '',
        api_token: '',
        default_warehouse_id: wmsStatus.default_warehouse_id || '',
        default_warehouse_name: wmsStatus.default_warehouse_name || '',
      });
    }
  }, [wmsStatus, isEditingWms]);

  useEffect(() => {
    if (isEditingStorage) {
      const provider = (storageStatus?.provider as StorageProvider) || 'ovh';
      setStorageFormData({
        provider,
        endpoint: storageStatus?.endpoint || (provider === 'ovh' ? storageDefaults?.endpoint : undefined) || STORAGE_PROVIDER_PRESETS[provider].endpoint,
        public_endpoint: storageStatus?.public_endpoint || '',
        bucket: storageStatus?.bucket || '',
        region: storageStatus?.region || (provider === 'ovh' ? storageDefaults?.region : undefined) || STORAGE_PROVIDER_PRESETS[provider].region,
        access_key: '',
        secret_key: '',
      });
    }
  }, [storageStatus, storageDefaults, isEditingStorage]);

  // Fetch geocoding settings
  const { data: geocodingSettings } = useQuery({
    queryKey: ['geocoding-settings'],
    queryFn: async () => {
      const response = await apiClient.client.get('/geocoding/settings');
      return response.data.data;
    },
    enabled: !!tenant && !user?.is_host,
  });

  // Save geocoding settings
  const saveGeocodingSettings = useMutation({
    mutationFn: async (data: { provider: 'nominatim' | 'google'; nominatim_url?: string; google_api_key?: string }) => {
      return apiClient.client.put('/geocoding/settings', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['geocoding-settings'] });
      toast.success('Configurazione geocodifica salvata');
      setIsEditingGeocoding(false);
    },
    onError: () => {
      toast.error('Errore nel salvataggio');
    },
  });

  useEffect(() => {
    if (geocodingSettings) {
      setGeocodingFormData({
        provider: geocodingSettings.provider || 'nominatim',
        nominatim_url: geocodingSettings.nominatim_url || '',
        google_api_key: '',
      });
    }
  }, [geocodingSettings]);

  // Fetch offer numbering settings
  const { data: numberingSettings, isLoading: numberingLoading } = useQuery<OfferNumberingSettings>({
    queryKey: ['offer-numbering-settings'],
    queryFn: async () => {
      const response = await offersAPI.getNumberingSettings();
      if (!response.data.data) throw new Error('Risposta non valida dal server');
      return response.data.data;
    },
    enabled: !!tenant && !user?.is_host && activeTab === 'numerazione',
  });

  useEffect(() => {
    if (numberingSettings) {
      setNumerazionePattern(numberingSettings.pattern);
    }
  }, [numberingSettings]);

  // Save offer numbering settings
  const saveNumberingSettings = useMutation({
    mutationFn: async (pattern: string) => {
      return offersAPI.updateNumberingSettings(pattern);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offer-numbering-settings'] });
      toast.success('Numerazione offerte salvata');
    },
    onError: (error: any) => {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Errore nel salvataggio della numerazione');
    },
  });

  const numerazioneTrimmed = numerazionePattern.trim();
  const numerazioneValid =
    numerazioneTrimmed.length > 0 &&
    numerazioneTrimmed.length <= NUMBERING_PATTERN_MAX_LENGTH &&
    /N/.test(numerazioneTrimmed);
  const numerazioneDirty = !!numberingSettings && numerazioneTrimmed !== numberingSettings.pattern;

  // Allegati Promozioni: tenant whitelist
  const { data: tenantMe, isLoading: tenantMeLoading } = useQuery({
    queryKey: ['tenant', 'me'],
    queryFn: async () => (await tenantsAPI.getMe()).data,
    enabled: !!tenant && !user?.is_host && isAdmin && (activeTab === 'allegati' || activeTab === 'assistente'),
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    const formats = tenantMe?.allowed_promo_attachment_formats || [];
    setAllegatiFormats(formats);
    setAllegatiInitialFormats(formats);
  }, [tenantMe]);

  const saveAllegatiFormats = useMutation({
    mutationFn: async (formats: string[]) =>
      (await tenantsAPI.updateMe({ allowed_promo_attachment_formats: formats })).data,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['tenant', 'me'] });
      const formats = data?.allowed_promo_attachment_formats || [];
      setAllegatiInitialFormats(formats);
      toast.success('Whitelist formati aggiornata');
    },
    onError: (error: any) => {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Errore nel salvataggio');
    },
  });

  const allegatiDirty =
    allegatiFormats.length !== allegatiInitialFormats.length ||
    allegatiFormats.some((f) => !allegatiInitialFormats.includes(f));
  const removedFormats = allegatiInitialFormats.filter((f) => !allegatiFormats.includes(f));

  // Fetch FIC connection status
  const { data: ficStatus, isLoading: ficLoading } = useQuery({
    queryKey: ['fic-status'],
    queryFn: async () => {
      const response = await apiClient.client.get('/fic/status');
      return response.data;
    },
    enabled: !!tenant && !user?.is_host,
  });

  // Initialize OAuth flow
  const initOAuth = useMutation({
    mutationFn: async () => {
      const response = await apiClient.client.get('/fic/auth/init');
      return response.data;
    },
    onSuccess: (data) => {
      // Store state for validation on return
      sessionStorage.setItem('fic_oauth_state', data.state);
      // Redirect to FIC authorization page
      window.location.href = data.authorization_url;
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore durante l\'autorizzazione');
    },
  });

  // Disconnect FIC
  const disconnectFIC = useMutation({
    mutationFn: async () => {
      const response = await apiClient.client.post('/fic/disconnect');
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fic-status'] });
      toast.success('Fatture in Cloud disconnesso con successo');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore durante la disconnessione');
    },
  });

  // ===== AI Configuration Queries and Mutations =====

  // Fetch AI status
  const { data: aiStatus, isLoading: aiStatusLoading } = useQuery<AIStatus>({
    queryKey: ['ai-status'],
    queryFn: async () => {
      const response = await apiClient.client.get('/ai/status');
      return response.data;
    },
    enabled: !!tenant,
  });

  // Fetch AI configuration
  const { data: aiConfig, isLoading: aiConfigLoading } = useQuery<AIConfig>({
    queryKey: ['ai-config'],
    queryFn: async () => {
      const response = await apiClient.client.get('/ai/config');
      return response.data;
    },
    enabled: !!tenant && aiStatus?.ai_configured === true,
    retry: false,
  });

  // Fetch AI usage stats
  const { data: aiUsageStats } = useQuery<AIUsageStats>({
    queryKey: ['ai-usage-stats'],
    queryFn: async () => {
      const response = await apiClient.client.get('/ai/usage/stats');
      return response.data;
    },
    enabled: !!tenant && aiStatus?.ai_configured === true,
    refetchInterval: 60000, // Refresh every minute
  });

  // Fetch AI usage history (monthly)
  const { data: aiUsageHistory } = useQuery<AIUsageHistoryMonth[]>({
    queryKey: ['ai-usage-history'],
    queryFn: async () => {
      const response = await aiAPI.getUsageHistory(6);
      return response.data.months;
    },
    enabled: !!tenant && aiStatus?.ai_configured === true,
  });

  // Il tenant può gestire la propria chiave AI solo se il piano lo consente
  const canEditAiConfig = !!user?.is_host || tenantMe?.piano?.feat_ai_custom_key === true;

  // AI Configuration form state
  const [isEditingAi, setIsEditingAi] = useState(false);
  const [aiFormData, setAiFormData] = useState({
    provider: 'openai' as 'openai' | 'anthropic' | 'ollama' | 'openrouter',
    model: 'gpt-4o',
    api_key: '',
    api_endpoint: '',
    ai_enabled: true,
    ai_suggestions_enabled: true,
    ai_generation_enabled: true,
    ai_improvement_enabled: true,
    temperature: 0.7,
  });

  useEffect(() => {
    if (aiConfig && isEditingAi) {
      setAiFormData({
        provider: aiConfig.provider,
        model: aiConfig.model,
        api_key: '',
        api_endpoint: aiConfig.api_endpoint || '',
        ai_enabled: aiConfig.ai_enabled,
        ai_suggestions_enabled: aiConfig.ai_suggestions_enabled,
        ai_generation_enabled: aiConfig.ai_generation_enabled,
        ai_improvement_enabled: aiConfig.ai_improvement_enabled,
        temperature: aiConfig.temperature,
      });
    }
  }, [aiConfig, isEditingAi]);

  const saveAiConfigMutation = useMutation({
    mutationFn: async (data: typeof aiFormData) => {
      const payload: any = { ...data };
      if (!payload.api_key) delete payload.api_key;
      if (!payload.api_endpoint) delete payload.api_endpoint;
      if (!user?.is_host) {
        // Campi riservati all'host: il tenant self-service può modificare
        // solo provider/modello/chiavi (vedi SELF_SERVICE_CONFIG_FIELDS backend)
        delete payload.ai_enabled;
        delete payload.ai_suggestions_enabled;
        delete payload.ai_generation_enabled;
        delete payload.ai_improvement_enabled;
        delete payload.temperature;
      }
      if (aiStatus?.ai_configured) {
        return apiClient.client.patch('/ai/config', payload);
      } else {
        return apiClient.client.post('/ai/config', payload);
      }
    },
    onSuccess: () => {
      toast.success('Configurazione AI salvata');
      setIsEditingAi(false);
      queryClient.invalidateQueries({ queryKey: ['ai-status'] });
      queryClient.invalidateQueries({ queryKey: ['ai-config'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    },
  });

  const MODEL_OPTIONS: Record<string, string[]> = {
    openai: ['gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo', 'gpt-3.5-turbo'],
    anthropic: ['claude-sonnet-4-6', 'claude-opus-4-6', 'claude-haiku-4-5-20251001'],
    ollama: ['llama3', 'mistral', 'codellama'],
    openrouter: [],
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Impostazioni</h1>
        <p className="text-gray-600 mt-1">Gestisci le configurazioni del sistema</p>
      </div>

      {/* Tab navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {visibleTabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.key
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'branding' && isAdmin && <TenantBrandingSettings />}
      {activeTab === 'integrazione' && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center">
              <KeyIcon className="h-6 w-6 text-gray-400 mr-3" />
              <h2 className="text-lg font-medium text-gray-900">Fatture in Cloud</h2>
            </div>
          </div>
          <div className="px-6 py-4">
            <div className="space-y-4">
              {/* Fatture in Cloud Integration */}
              <div className="p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-gray-900">Connessione OAuth</h3>
                    <p className="text-sm text-gray-500">Sincronizza clienti, prodotti e preventivi</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    {ficLoading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
                    ) : ficStatus?.connected ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Connesso
                      </span>
                    ) : ficStatus?.credentials_configured ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        Credenziali configurate
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        Non configurato
                      </span>
                    )}
                  </div>
                </div>

                {!ficStatus?.credentials_configured && <p className="text-sm text-gray-500">L’host deve configurare le credenziali dell’app Fatture in Cloud prima di collegare il tuo account.</p>}

                {/* Step 2: Connect to FIC */}
                {ficStatus?.credentials_configured && !ficStatus?.connected && (
                  <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-4">
                    <h4 className="text-sm font-medium text-green-800 mb-2">Passo 2: Connetti a Fatture in Cloud</h4>
                    <p className="text-xs text-green-700 mb-3">
                      Le credenziali sono configurate. Ora puoi autorizzare l'accesso al tuo account Fatture in Cloud.
                    </p>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => initOAuth.mutate()}
                        disabled={initOAuth.isPending}
                        className="px-3 py-1.5 text-xs border border-green-300 rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                      >
                        {initOAuth.isPending ? 'Connessione...' : 'Connetti a FIC'}
                      </button>

                    </div>
                  </div>
                )}

                {/* Connected status */}
                {ficStatus?.connected && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm mb-4">
                      {ficStatus.company_id && (
                        <div>
                          <dt className="text-gray-500">Company ID</dt>
                          <dd className="text-gray-900 font-medium">{ficStatus.company_id}</dd>
                        </div>
                      )}
                      {ficStatus.token_expires_at && (
                        <div>
                          <dt className="text-gray-500">Token scade il</dt>
                          <dd className="text-gray-900 font-medium">
                            {new Date(ficStatus.token_expires_at).toLocaleDateString('it-IT')}
                          </dd>
                        </div>
                      )}
                    </dl>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => disconnectFIC.mutate()}
                        disabled={disconnectFIC.isPending}
                        className="px-3 py-1.5 text-xs border border-yellow-300 rounded-md text-yellow-700 hover:bg-yellow-50 disabled:opacity-50"
                      >
                        Disconnetti
                      </button>

                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'assistente' && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center">
              <SparklesIcon className="h-6 w-6 text-purple-500 mr-3" />
              <h2 className="text-lg font-medium text-gray-900">Assistente AI</h2>
            </div>
          </div>
          <div className="px-6 py-4">
            <div className="space-y-4">
              {/* AI Status Card */}
              <div className="p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-gray-900">Configurazione LLM</h3>
                    <p className="text-sm text-gray-500">Configura l'assistente AI per la generazione di offerte</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    {aiStatusLoading || aiConfigLoading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600"></div>
                    ) : aiStatus?.ai_configured && aiStatus?.ai_enabled ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Attivo
                      </span>
                    ) : aiStatus?.ai_configured ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        Configurato (disabilitato)
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        Non configurato
                      </span>
                    )}
                  </div>
                </div>

                {/* Current Configuration Display */}
                {aiStatus?.ai_configured && aiConfig && (
                  <div className="space-y-4">
                    {/* Provider Info */}
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      <div>
                        <dt className="text-gray-500">Provider</dt>
                        <dd className="text-gray-900 font-medium">{PROVIDER_LABELS[aiConfig.provider]}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Modello</dt>
                        <dd className="text-gray-900 font-medium">{aiConfig.model}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">API Key</dt>
                        <dd className="text-gray-900 font-medium">
                          {aiConfig.has_api_key ? '••••••••••••' : 'Non configurata'}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Fallback</dt>
                        <dd className="text-gray-900 font-medium">
                          {aiConfig.fallback_provider
                            ? `${PROVIDER_LABELS[aiConfig.fallback_provider]} - ${aiConfig.fallback_model}`
                            : 'Non configurato'}
                        </dd>
                      </div>
                    </dl>

                    {/* Feature Flags */}
                    <div className="pt-3 border-t border-gray-100">
                      <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Funzionalità abilitate</h4>
                      <div className="flex flex-wrap gap-2">
                        {aiConfig.ai_suggestions_enabled && (
                          <span className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-purple-50 text-purple-700">
                            Suggerimenti
                          </span>
                        )}
                        {aiConfig.ai_generation_enabled && (
                          <span className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-purple-50 text-purple-700">
                            Generazione
                          </span>
                        )}
                        {aiConfig.ai_improvement_enabled && (
                          <span className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-purple-50 text-purple-700">
                            Miglioramento
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Usage Stats */}
                    {aiUsageStats && (
                      <div className="pt-3 border-t border-gray-100">
                        <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Utilizzo</h4>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="bg-gray-50 rounded-md p-3">
                            <p className="text-xs text-gray-500">Richieste (questa ora)</p>
                            <p className="text-lg font-semibold text-gray-900">
                              {aiUsageStats.requests_this_hour}
                              {aiUsageStats.limits && (
                                <span className="text-sm text-gray-500 font-normal">
                                  /{aiUsageStats.limits.max_requests_per_hour}
                                </span>
                              )}
                            </p>
                            {aiUsageStats.requests_remaining_this_hour != null && (
                              <p className="text-xs text-gray-500">
                                {aiUsageStats.requests_remaining_this_hour} rimanenti
                              </p>
                            )}
                          </div>
                          <div className="bg-gray-50 rounded-md p-3">
                            <p className="text-xs text-gray-500">Token (questo mese)</p>
                            <p className="text-lg font-semibold text-gray-900">
                              {(aiUsageStats.tokens_this_month / 1000).toFixed(1)}K
                              {aiUsageStats.limits && (
                                <span className="text-sm text-gray-500 font-normal">
                                  /{(aiUsageStats.limits.max_tokens_per_month / 1000000).toFixed(1)}M
                                </span>
                              )}
                            </p>
                            {aiUsageStats.tokens_remaining_this_month != null && (
                              <p className="text-xs text-gray-500">
                                {aiUsageStats.tokens_remaining_this_month.toLocaleString('it-IT')} token rimanenti
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Monthly History */}
                    {aiUsageHistory && aiUsageHistory.length > 0 && (
                      <div className="pt-3 border-t border-gray-100">
                        <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Storico mensile</h4>
                        <table className="min-w-full text-sm">
                          <thead>
                            <tr className="text-left text-xs text-gray-500">
                              <th className="py-1 pr-4 font-medium">Mese</th>
                              <th className="py-1 pr-4 font-medium">Token</th>
                              <th className="py-1 pr-4 font-medium">Richieste</th>
                              <th className="py-1 font-medium">Riuscite</th>
                            </tr>
                          </thead>
                          <tbody>
                            {[...aiUsageHistory].reverse().map((m) => (
                              <tr key={m.month} className="border-t border-gray-100">
                                <td className="py-1 pr-4 text-gray-900">{m.month}</td>
                                <td className="py-1 pr-4 text-gray-900">{m.total_tokens.toLocaleString('it-IT')}</td>
                                <td className="py-1 pr-4 text-gray-900">{m.requests}</td>
                                <td className="py-1 text-gray-900">{m.success_count}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                  </div>
                )}

                {/* Configure / Edit Button */}
                {!isEditingAi && canEditAiConfig && (
                  <div className="pt-3">
                    <button
                      onClick={() => setIsEditingAi(true)}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      {aiStatus?.ai_configured ? 'Modifica configurazione' : 'Configura AI'}
                    </button>
                  </div>
                )}

                {/* Banner: configurazione gestita dall'host */}
                {!user?.is_host && !canEditAiConfig && (
                  <div className="pt-3">
                    <div className="text-sm bg-blue-50 border border-blue-200 text-blue-800 rounded-lg px-3 py-2">
                      La configurazione AI è gestita dall'amministratore della piattaforma.
                    </div>
                  </div>
                )}

                {/* AI Configuration Form */}
                {isEditingAi && canEditAiConfig && (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      saveAiConfigMutation.mutate(aiFormData);
                    }}
                    className="mt-4 space-y-4 p-4 border border-purple-200 rounded-lg bg-purple-50"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Provider</label>
                        <select
                          value={aiFormData.provider}
                          onChange={(e) => {
                            const provider = e.target.value as 'openai' | 'anthropic' | 'ollama' | 'openrouter';
                            setAiFormData({
                              ...aiFormData,
                              provider,
                              model: provider === 'openrouter'
                                ? 'openai/gpt-4o-mini'
                                : MODEL_OPTIONS[provider]?.[0] || '',
                            });
                          }}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                        >
                          <option value="openai">OpenAI</option>
                          {user?.is_host && <option value="anthropic">Anthropic (Claude)</option>}
                          {user?.is_host && <option value="ollama">Ollama (Locale)</option>}
                          <option value="openrouter">OpenRouter</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Modello</label>
                        {aiFormData.provider === 'openrouter' ? (
                          <input
                            type="text"
                            value={aiFormData.model}
                            onChange={(e) => setAiFormData({ ...aiFormData, model: e.target.value })}
                            placeholder="openai/gpt-4o-mini"
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                          />
                        ) : (
                          <select
                            value={aiFormData.model}
                            onChange={(e) => setAiFormData({ ...aiFormData, model: e.target.value })}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                          >
                            {(MODEL_OPTIONS[aiFormData.provider] || []).map((m) => (
                              <option key={m} value={m}>{m}</option>
                            ))}
                          </select>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        API Key {aiStatus?.ai_configured && <span className="text-gray-400">(lascia vuoto per mantenere)</span>}
                      </label>
                      <input
                        type="password"
                        value={aiFormData.api_key}
                        onChange={(e) => setAiFormData({ ...aiFormData, api_key: e.target.value })}
                        placeholder={aiConfig?.has_api_key ? '••••••••••••' : 'Inserisci API Key'}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                      />
                    </div>

                    {(aiFormData.provider === 'ollama' || aiFormData.provider === 'openrouter') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Endpoint API</label>
                        <input
                          type="text"
                          value={aiFormData.api_endpoint}
                          onChange={(e) => setAiFormData({ ...aiFormData, api_endpoint: e.target.value })}
                          placeholder={aiFormData.provider === 'openrouter' ? 'https://openrouter.ai/api/v1' : 'http://localhost:11434'}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                        />
                      </div>
                    )}

                    {user?.is_host && (
                      <div className="flex flex-wrap gap-4 pt-2">
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={aiFormData.ai_enabled}
                          onChange={(e) => setAiFormData({ ...aiFormData, ai_enabled: e.target.checked })}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        AI Abilitata
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={aiFormData.ai_suggestions_enabled}
                          onChange={(e) => setAiFormData({ ...aiFormData, ai_suggestions_enabled: e.target.checked })}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        Suggerimenti
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={aiFormData.ai_generation_enabled}
                          onChange={(e) => setAiFormData({ ...aiFormData, ai_generation_enabled: e.target.checked })}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        Generazione
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={aiFormData.ai_improvement_enabled}
                          onChange={(e) => setAiFormData({ ...aiFormData, ai_improvement_enabled: e.target.checked })}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        Miglioramento
                      </label>
                      </div>
                    )}

                    <div className="flex gap-3 pt-2">
                      <button
                        type="submit"
                        disabled={saveAiConfigMutation.isPending}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50"
                      >
                        {saveAiConfigMutation.isPending ? 'Salvataggio...' : 'Salva'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setIsEditingAi(false)}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                      >
                        Annulla
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'geocodifica' && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <MapPinIcon className="h-6 w-6 text-gray-400 mr-3" />
                <div>
                  <h2 className="text-lg font-medium text-gray-900">Geocodifica</h2>
                  <p className="text-sm text-gray-500">Servizio di localizzazione per gli indirizzi clienti</p>
                </div>
              </div>
              {!isEditingGeocoding && (
                <button
                  onClick={() => setIsEditingGeocoding(true)}
                  className="px-3 py-1.5 text-xs border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Configura
                </button>
              )}
            </div>
          </div>
          <div className="px-6 py-4">
            {!isEditingGeocoding ? (
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Provider</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {geocodingSettings?.provider === 'google' ? 'Google Maps' : 'Nominatim (OpenStreetMap)'}
                  </dd>
                </div>
                {geocodingSettings?.provider === 'nominatim' && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">URL Nominatim</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {geocodingSettings?.nominatim_url || 'Pubblico (nominatim.openstreetmap.org)'}
                    </dd>
                  </div>
                )}
                {geocodingSettings?.provider === 'google' && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">API Key Google</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {geocodingSettings?.has_google_api_key ? '••••••••••••' : 'Non configurata'}
                    </dd>
                  </div>
                )}
              </dl>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  saveGeocodingSettings.mutate({
                    provider: geocodingFormData.provider,
                    nominatim_url: geocodingFormData.provider === 'nominatim' ? geocodingFormData.nominatim_url : undefined,
                    google_api_key: geocodingFormData.provider === 'google' ? geocodingFormData.google_api_key : undefined,
                  });
                }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Provider</label>
                  <select
                    value={geocodingFormData.provider}
                    onChange={(e) => setGeocodingFormData({ ...geocodingFormData, provider: e.target.value as 'nominatim' | 'google' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  >
                    <option value="nominatim">Nominatim (OpenStreetMap)</option>
                    <option value="google">Google Maps</option>
                  </select>
                </div>

                {geocodingFormData.provider === 'nominatim' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">URL Nominatim (opzionale)</label>
                    <input
                      type="url"
                      value={geocodingFormData.nominatim_url}
                      onChange={(e) => setGeocodingFormData({ ...geocodingFormData, nominatim_url: e.target.value })}
                      placeholder="https://nominatim.openstreetmap.org"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Lascia vuoto per usare il servizio pubblico. Inserisci l'URL per un'istanza Nominatim privata.
                    </p>
                  </div>
                )}

                {geocodingFormData.provider === 'google' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">API Key Google Maps</label>
                    <div className="relative">
                      <input
                        type={showGoogleKey ? 'text' : 'password'}
                        value={geocodingFormData.google_api_key}
                        onChange={(e) => setGeocodingFormData({ ...geocodingFormData, google_api_key: e.target.value })}
                        placeholder={geocodingSettings?.has_google_api_key ? '••••••••••••' : 'Inserisci API Key'}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowGoogleKey(!showGoogleKey)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        {showGoogleKey ? (
                          <EyeSlashIcon className="h-4 w-4 text-gray-400" />
                        ) : (
                          <EyeIcon className="h-4 w-4 text-gray-400" />
                        )}
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Richiede l'API Geocoding abilitata nel progetto Google Cloud.
                    </p>
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setIsEditingGeocoding(false)}
                    className="px-3 py-1.5 text-xs border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Annulla
                  </button>
                  <button
                    type="submit"
                    disabled={saveGeocodingSettings.isPending}
                    className="px-3 py-1.5 text-xs border border-blue-300 rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                  >
                    {saveGeocodingSettings.isPending ? 'Salvataggio...' : 'Salva'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
      {activeTab === 'numerazione' && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center">
              <HashtagIcon className="h-6 w-6 text-gray-400 mr-3" />
              <div>
                <h2 className="text-lg font-medium text-gray-900">Numerazione offerte</h2>
                <p className="text-sm text-gray-500">Formato del numero progressivo assegnato alle nuove offerte</p>
              </div>
            </div>
          </div>
          <div className="px-6 py-4">
            {numberingLoading ? (
              <div className="flex justify-center py-6">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!numerazioneValid || !numerazioneDirty) return;
                  saveNumberingSettings.mutate(numerazioneTrimmed);
                }}
                className="space-y-4"
              >
                <div>
                  <label htmlFor="numerazione_pattern" className="block text-sm font-medium text-gray-700 mb-1">
                    Pattern di numerazione
                  </label>
                  <input
                    type="text"
                    id="numerazione_pattern"
                    value={numerazionePattern}
                    onChange={(e) => setNumerazionePattern(e.target.value)}
                    maxLength={NUMBERING_PATTERN_MAX_LENGTH}
                    placeholder="YYYY-NNN"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm font-mono"
                  />
                  {numerazioneTrimmed.length > 0 && !/N/.test(numerazioneTrimmed) && (
                    <p className="mt-1 text-xs text-red-600">
                      Il pattern deve contenere almeno un gruppo di N (il progressivo).
                    </p>
                  )}
                </div>

                <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                  <div className="flex items-baseline justify-between">
                    <span className="text-sm font-medium text-gray-500">Anteprima prossimo numero</span>
                    <span className="text-lg font-semibold text-gray-900 font-mono">
                      {numberingSettings && numerazioneValid
                        ? renderNumberingPattern(numerazioneTrimmed, numberingSettings.anno, numberingSettings.prossimo_seq)
                        : '—'}
                    </span>
                  </div>
                  {numberingSettings?.esempio && (
                    <p className="mt-2 text-xs text-gray-500">
                      Formato attualmente salvato: <span className="font-mono">{numberingSettings.esempio}</span>
                    </p>
                  )}
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                  <h4 className="text-sm font-medium text-blue-800 mb-2">Placeholder disponibili</h4>
                  <ul className="text-xs text-blue-700 space-y-1 list-disc list-inside">
                    <li><span className="font-mono">YYYY</span> — anno a 4 cifre (es. 2026)</li>
                    <li><span className="font-mono">YY</span> — anno a 2 cifre (es. 26)</li>
                    <li>
                      <span className="font-mono">N</span>, <span className="font-mono">NN</span>, <span className="font-mono">NNN</span>, ... — progressivo
                      con zeri iniziali pari al numero di N (es. <span className="font-mono">NNNNN</span> con progressivo 42 → <span className="font-mono">00042</span>)
                    </li>
                  </ul>
                  <p className="mt-2 text-xs text-blue-700">
                    Il progressivo riparte automaticamente da 1 a ogni nuovo anno. Evita la lettera N maiuscola nel
                    testo fisso: verrebbe interpretata come placeholder. Lunghezza massima: {NUMBERING_PATTERN_MAX_LENGTH} caratteri.
                  </p>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setNumerazionePattern(numberingSettings?.pattern || '')}
                    disabled={!numerazioneDirty}
                    className="px-3 py-1.5 text-xs border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Annulla
                  </button>
                  <button
                    type="submit"
                    disabled={!numerazioneValid || !numerazioneDirty || saveNumberingSettings.isPending}
                    className="px-3 py-1.5 text-xs border border-blue-300 rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                  >
                    {saveNumberingSettings.isPending ? 'Salvataggio...' : 'Salva'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
      {activeTab === 'magazzino' && (
        <div className="space-y-6">
          {wmsStatus && !wmsStatus.wms_enabled && <p className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">La funzione WMS è disabilitata. Chiedi all’admin host di abilitarla e associare il tenant Warehouse.</p>}
          {/* Connection Status Card */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <CubeIcon className="h-6 w-6 text-gray-400 mr-3" />
                  <div>
                    <h2 className="text-lg font-medium text-gray-900">Connessione WMS</h2>
                    <p className="text-sm text-gray-500">Integrazione con il sistema di gestione magazzino</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {wmsLoading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                  ) : wmsStatus?.connected ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Connesso
                    </span>
                  ) : wmsStatus?.configured ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      Configurato
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      Non configurato
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="px-6 py-4">
              {!isEditingWms ? (
                <div className="space-y-4">
                  {wmsStatus?.configured && (
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      <div>
                        <dt className="text-gray-500">URL Base</dt>
                        <dd className="text-gray-900 font-medium">{wmsStatus.base_url}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Magazzino Default</dt>
                        <dd className="text-gray-900 font-medium">{wmsStatus.default_warehouse_name || wmsStatus.default_warehouse_id || 'Non impostato'}</dd>
                      </div>
                      {wmsStatus.last_sync_at && (
                        <div>
                          <dt className="text-gray-500">Ultima Sincronizzazione</dt>
                          <dd className="text-gray-900 font-medium">{new Date(wmsStatus.last_sync_at).toLocaleString('it-IT')}</dd>
                        </div>
                      )}
                      {wmsStatus.last_error && (
                        <div className="sm:col-span-2">
                          <dt className="text-gray-500">Ultimo Errore</dt>
                          <dd className="text-red-600 text-xs">{wmsStatus.last_error}</dd>
                        </div>
                      )}
                    </dl>
                  )}
                  <div className="flex space-x-2">
                    <button
                      disabled={!wmsStatus?.wms_enabled}
                      onClick={() => setIsEditingWms(true)}
                      className="px-3 py-1.5 text-xs border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                    >
                      {wmsStatus?.configured ? 'Modifica' : 'Configura'}
                    </button>
                    {wmsStatus?.configured && (
                      <>
                        <button
                          onClick={() => testWmsConnection.mutate()}
                          disabled={testWmsConnection.isPending}
                          className="px-3 py-1.5 text-xs border border-blue-300 rounded-md text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                        >
                          {testWmsConnection.isPending ? 'Test...' : 'Test Connessione'}
                        </button>
                        <button
                          onClick={() => disconnectWms.mutate()}
                          disabled={disconnectWms.isPending}
                          className="px-3 py-1.5 text-xs border border-red-300 rounded-md text-red-700 hover:bg-red-50 disabled:opacity-50"
                        >
                          Disconnetti
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!wmsFormData.base_url) {
                      toast.error('Inserisci l\'URL base del WMS');
                      return;
                    }
                    if (wmsFormData.auth_mode === 'api_token' && !wmsStatus?.configured && !wmsFormData.api_token) {
                      toast.error('Inserisci il token API');
                      return;
                    }
                    saveWmsConfig.mutate({
                      auth_mode: wmsFormData.auth_mode,
                      ...(wmsFormData.auth_mode === 'client_credentials' ? {
                        oauth_authority: wmsFormData.oauth_authority,
                        oauth_client_id: wmsFormData.oauth_client_id,
                        oauth_client_secret: wmsFormData.oauth_client_secret || undefined,
                        oauth_tenant_id: wmsFormData.oauth_tenant_id,
                      } : {}),
                      base_url: wmsFormData.base_url,
                      api_token: wmsFormData.auth_mode === 'api_token' ? wmsFormData.api_token || undefined : undefined,
                      default_warehouse_id: wmsFormData.default_warehouse_id || undefined,
                      default_warehouse_name: wmsFormData.default_warehouse_name || undefined,
                    });
                  }}
                  className="space-y-4"
                >
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">URL Base WMS</label>
                    <input
                      type="url"
                      value={wmsFormData.base_url}
                      onChange={(e) => setWmsFormData({ ...wmsFormData, base_url: e.target.value })}
                      placeholder="https://wms.example.com/api"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="wms-auth-mode" className="block text-sm font-medium text-gray-700 mb-1">Autenticazione</label>
                    <select id="wms-auth-mode" value={wmsFormData.auth_mode} onChange={(e) => setWmsFormData({ ...wmsFormData, auth_mode: e.target.value as 'api_token' | 'client_credentials' })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                      <option value="api_token">API token</option>
                      <option value="client_credentials">OAuth — integrazione tra sistemi</option>
                    </select>
                  </div>
                  {wmsFormData.auth_mode === 'client_credentials' && <>
                    {([['oauth_authority', 'Authority OAuth', 'https://auth.clayapps.it'], ['oauth_client_id', 'Client ID dedicato', 'Offertify_MEPA'], ['oauth_tenant_id', 'ID tenant WMS', 'Associazione configurata dall’admin host'], ['oauth_client_secret', 'Client secret', wmsStatus?.has_oauth_client_secret ? 'Lascia vuoto per mantenere il secret' : 'Inserisci client secret']] as const).map(([field, label, placeholder]) => (
                      <div key={field}>
                        <label htmlFor={`wms-${field}`} className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                        <input id={`wms-${field}`} readOnly={field === 'oauth_tenant_id'} type={field === 'oauth_client_secret' ? 'password' : 'text'} autoComplete="off" value={wmsFormData[field]} onChange={(e) => setWmsFormData({ ...wmsFormData, [field]: e.target.value })} placeholder={placeholder} required={field !== 'oauth_client_secret' || !wmsStatus?.has_oauth_client_secret} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                      </div>
                    ))}
                    <p className="text-xs text-gray-500">Usa un client dedicato autorizzato al tenant WMS. Il secret viene salvato cifrato e non viene restituito dalla API.</p>
                  </>}
                  {wmsFormData.auth_mode === 'api_token' && <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      API Token {wmsStatus?.configured && '(lascia vuoto per mantenere)'}
                    </label>
                    <div className="relative">
                      <input
                        type={showWmsToken ? 'text' : 'password'}
                        value={wmsFormData.api_token}
                        onChange={(e) => setWmsFormData({ ...wmsFormData, api_token: e.target.value })}
                        placeholder={wmsStatus?.configured ? '••••••••••••' : 'Inserisci API Token'}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowWmsToken(!showWmsToken)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        {showWmsToken ? (
                          <EyeSlashIcon className="h-4 w-4 text-gray-400" />
                        ) : (
                          <EyeIcon className="h-4 w-4 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Magazzino Default</label>
                    {wmsWarehouses && wmsWarehouses.length > 0 ? (
                      <select
                        value={wmsFormData.default_warehouse_id}
                        onChange={(e) => {
                          const wh = wmsWarehouses.find((w: { id: string; name: string }) => w.id === e.target.value);
                          setWmsFormData({
                            ...wmsFormData,
                            default_warehouse_id: e.target.value,
                            default_warehouse_name: wh?.name || '',
                          });
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      >
                        <option value="">Seleziona magazzino</option>
                        {wmsWarehouses.map((wh: { id: string; name: string }) => (
                          <option key={wh.id} value={wh.id}>{wh.name}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={wmsFormData.default_warehouse_id}
                        onChange={(e) => setWmsFormData({ ...wmsFormData, default_warehouse_id: e.target.value })}
                        placeholder="ID magazzino"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                    )}
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <button
                      type="button"
                      onClick={() => { setWmsFormData((previous) => ({ ...previous, api_token: '', oauth_client_secret: '' })); setIsEditingWms(false); }}
                      className="px-3 py-1.5 text-xs border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                    >
                      Annulla
                    </button>
                    <button
                      type="submit"
                      disabled={saveWmsConfig.isPending}
                      className="px-3 py-1.5 text-xs border border-blue-300 rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                    >
                      {saveWmsConfig.isPending ? 'Salvataggio...' : 'Salva'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>

          {/* Order Settings */}
          {wmsStatus?.configured && (
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Impostazioni Ordini</h3>
              </div>
              <div className="px-6 py-4 space-y-4">
                <label className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-medium text-gray-900">Crea ordine automaticamente all'accettazione</span>
                    <p className="text-xs text-gray-500">Quando un'offerta viene accettata, crea automaticamente un ordine nel WMS</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      wmsAPI.updateConfig({ auto_create_order: !wmsStatus.auto_create_order }).then(() => {
                        queryClient.invalidateQueries({ queryKey: ['wms-status'] });
                        toast.success('Impostazione aggiornata');
                      }).catch(() => toast.error('Errore nell\'aggiornamento'));
                    }}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                      wmsStatus.auto_create_order ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                  >
                    <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      wmsStatus.auto_create_order ? 'translate-x-5' : 'translate-x-0'
                    }`} />
                  </button>
                </label>
                <label className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-medium text-gray-900">Richiedi approvazione ordine (modalità supervisionata)</span>
                    <p className="text-xs text-gray-500">L'ordine viene creato in stato DRAFT e richiede conferma manuale</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      wmsAPI.updateConfig({ order_requires_approval: !wmsStatus.order_requires_approval }).then(() => {
                        queryClient.invalidateQueries({ queryKey: ['wms-status'] });
                        toast.success('Impostazione aggiornata');
                      }).catch(() => toast.error('Errore nell\'aggiornamento'));
                    }}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                      wmsStatus.order_requires_approval ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                  >
                    <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      wmsStatus.order_requires_approval ? 'translate-x-5' : 'translate-x-0'
                    }`} />
                  </button>
                </label>
              </div>
            </div>
          )}

          {/* Mapping Table */}
          {wmsStatus?.configured && (
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">Mappatura Prodotti</h3>
                  <div className="flex gap-2">
                    <button
                      onClick={() => wmsAutoMatch.mutate()}
                      disabled={wmsAutoMatch.isPending}
                      className="px-3 py-1.5 text-xs border border-green-300 rounded-md text-green-700 hover:bg-green-50 disabled:opacity-50"
                    >
                      {wmsAutoMatch.isPending ? 'Auto-match...' : 'Auto-Match'}
                    </button>
                    <button
                      onClick={() => setShowMappingModal(true)}
                      className="px-3 py-1.5 text-xs border border-blue-300 rounded-md text-white bg-blue-600 hover:bg-blue-700"
                    >
                      Nuovo Mapping
                    </button>
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prodotto</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Codice WMS</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome WMS</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Tipo</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Azioni</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {(wmsMappingsData?.data || []).map((mapping: WmsMapping) => (
                      <tr key={mapping.id}>
                        <td className="px-6 py-3 text-sm">
                          <div className="font-medium text-gray-900">{mapping.prodotto_nome}</div>
                          {mapping.prodotto_codice && (
                            <div className="text-xs text-gray-500">{mapping.prodotto_codice}</div>
                          )}
                        </td>
                        <td className="px-6 py-3 text-sm font-mono text-gray-700">{mapping.wms_material_code}</td>
                        <td className="px-6 py-3 text-sm text-gray-700">{mapping.wms_material_name || '-'}</td>
                        <td className="px-6 py-3 text-center">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            mapping.is_auto_matched ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {mapping.is_auto_matched ? 'Auto' : 'Manuale'}
                          </span>
                        </td>
                        <td className="px-6 py-3 text-right">
                          <button
                            onClick={() => {
                              if (confirm('Eliminare questo mapping?')) {
                                deleteWmsMapping.mutate(mapping.id);
                              }
                            }}
                            className="text-red-600 hover:text-red-900 text-xs"
                          >
                            Elimina
                          </button>
                        </td>
                      </tr>
                    ))}
                    {(!wmsMappingsData?.data || wmsMappingsData.data.length === 0) && (
                      <tr>
                        <td colSpan={5} className="px-6 py-8 text-center text-sm text-gray-500">
                          Nessun mapping configurato. Usa "Auto-Match" o crea un mapping manualmente.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {wmsMappingsData?.pagination && wmsMappingsData.pagination.pages > 1 && (
                <div className="px-6 py-3 border-t border-gray-200 flex justify-between items-center">
                  <span className="text-sm text-gray-500">
                    Pagina {wmsMappingsData.pagination.page} di {wmsMappingsData.pagination.pages}
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setMappingPage(p => Math.max(1, p - 1))}
                      disabled={mappingPage <= 1}
                      className="px-3 py-1 text-xs border border-gray-300 rounded-md disabled:opacity-50"
                    >
                      Precedente
                    </button>
                    <button
                      onClick={() => setMappingPage(p => p + 1)}
                      disabled={mappingPage >= wmsMappingsData.pagination.pages}
                      className="px-3 py-1 text-xs border border-gray-300 rounded-md disabled:opacity-50"
                    >
                      Successivo
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === 'storage' && isAdmin && (
        <div className="space-y-6">
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
            Lo storage S3 è obbligatorio per questo tenant: collega il tuo bucket (OVH, AWS o
            Google Cloud) con credenziali dedicate. Senza configurazione, il caricamento di
            immagini prodotto e la generazione dei PDF non sono disponibili.
          </div>

          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <CircleStackIcon className="h-6 w-6 text-gray-400 mr-3" />
                  <div>
                    <h2 className="text-lg font-medium text-gray-900">Storage S3</h2>
                    <p className="text-sm text-gray-500">Bucket object storage per file, immagini e PDF di questo tenant</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {storageLoading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                  ) : storageStatus?.connected ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Connesso</span>
                  ) : storageStatus?.configured ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Configurato</span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Non configurato</span>
                  )}
                </div>
              </div>
            </div>
            <div className="px-6 py-4">
              {!isEditingStorage ? (
                <div className="space-y-4">
                  {storageStatus?.configured && (
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      <div>
                        <dt className="text-gray-500">Provider</dt>
                        <dd className="text-gray-900 font-medium">{storageStatus.provider ? STORAGE_PROVIDER_PRESETS[storageStatus.provider].label : '-'}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Bucket</dt>
                        <dd className="text-gray-900 font-medium">{storageStatus.bucket}</dd>
                      </div>
                      <div className="sm:col-span-2">
                        <dt className="text-gray-500">Endpoint</dt>
                        <dd className="text-gray-900 font-medium break-all">{storageStatus.endpoint}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Region</dt>
                        <dd className="text-gray-900 font-medium">{storageStatus.region}</dd>
                      </div>
                      {storageStatus.last_tested_at && (
                        <div>
                          <dt className="text-gray-500">Ultimo test</dt>
                          <dd className="text-gray-900 font-medium">{new Date(storageStatus.last_tested_at).toLocaleString('it-IT')}</dd>
                        </div>
                      )}
                      {storageStatus.last_error && (
                        <div className="sm:col-span-2">
                          <dt className="text-gray-500">Ultimo Errore</dt>
                          <dd className="text-red-600 text-xs break-all">{storageStatus.last_error}</dd>
                        </div>
                      )}
                    </dl>
                  )}
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setIsEditingStorage(true)}
                      className="px-3 py-1.5 text-xs border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                    >
                      {storageStatus?.configured ? 'Modifica' : 'Configura'}
                    </button>
                    {storageStatus?.configured && (
                      <>
                        <button
                          onClick={() => createStorageBucket.mutate()}
                          disabled={createStorageBucket.isPending}
                          className="px-3 py-1.5 text-xs border border-blue-300 rounded-md text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                        >
                          {createStorageBucket.isPending ? 'Creazione...' : 'Crea bucket'}
                        </button>
                        <button
                          onClick={() => testStorageConnection.mutate()}
                          disabled={testStorageConnection.isPending}
                          className="px-3 py-1.5 text-xs border border-blue-300 rounded-md text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                        >
                          {testStorageConnection.isPending ? 'Test...' : 'Test Connessione'}
                        </button>
                        <button
                          onClick={() => disconnectStorage.mutate()}
                          disabled={disconnectStorage.isPending}
                          className="px-3 py-1.5 text-xs border border-red-300 rounded-md text-red-700 hover:bg-red-50 disabled:opacity-50"
                        >
                          Disconnetti
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!storageFormData.endpoint || !storageFormData.bucket || (!storageStatus?.configured && !storageFormData.access_key)) {
                      toast.error('Compila endpoint, bucket e access key');
                      return;
                    }
                    if (!storageStatus?.configured && !storageFormData.secret_key) {
                      toast.error('Inserisci la secret key');
                      return;
                    }
                    saveStorageConfig.mutate({
                      provider: storageFormData.provider,
                      endpoint: storageFormData.endpoint,
                      public_endpoint: storageFormData.public_endpoint || undefined,
                      bucket: storageFormData.bucket,
                      region: storageFormData.region || undefined,
                      access_key: storageFormData.access_key || undefined,
                      secret_key: storageFormData.secret_key || undefined,
                    });
                  }}
                  className="space-y-4"
                >
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Provider</label>
                    <select
                      value={storageFormData.provider}
                      onChange={(e) => {
                        const provider = e.target.value as StorageProvider;
                        const preset = STORAGE_PROVIDER_PRESETS[provider];
                        setStorageFormData({
                          ...storageFormData,
                          provider,
                          endpoint: storageFormData.endpoint || preset.endpoint,
                          region: storageFormData.region || preset.region,
                        });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    >
                      <option value="ovh">OVH Object Storage</option>
                      <option value="aws">Amazon S3</option>
                      <option value="gcs">Google Cloud Storage</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Endpoint</label>
                    <input
                      type="url"
                      value={storageFormData.endpoint}
                      onChange={(e) => setStorageFormData({ ...storageFormData, endpoint: e.target.value })}
                      placeholder={STORAGE_PROVIDER_PRESETS[storageFormData.provider].endpoint}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Bucket</label>
                      <input
                        type="text"
                        value={storageFormData.bucket}
                        onChange={(e) => setStorageFormData({ ...storageFormData, bucket: e.target.value })}
                        placeholder="nome-bucket"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Region</label>
                      <input
                        type="text"
                        value={storageFormData.region}
                        onChange={(e) => setStorageFormData({ ...storageFormData, region: e.target.value })}
                        placeholder={STORAGE_PROVIDER_PRESETS[storageFormData.provider].region}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Access Key</label>
                    <input
                      type="text"
                      value={storageFormData.access_key}
                      onChange={(e) => setStorageFormData({ ...storageFormData, access_key: e.target.value })}
                      placeholder={storageStatus?.has_access_key ? '(salvata — reinserisci per cambiare)' : 'Access key ID'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Secret Key {storageStatus?.has_secret_key && '(lascia vuoto per mantenere)'}
                    </label>
                    <div className="relative">
                      <input
                        type={showStorageSecret ? 'text' : 'password'}
                        value={storageFormData.secret_key}
                        onChange={(e) => setStorageFormData({ ...storageFormData, secret_key: e.target.value })}
                        placeholder={storageStatus?.has_secret_key ? '••••••••••••' : 'Secret access key'}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowStorageSecret(!showStorageSecret)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        {showStorageSecret ? (
                          <EyeSlashIcon className="h-4 w-4 text-gray-400" />
                        ) : (
                          <EyeIcon className="h-4 w-4 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Endpoint pubblico (opzionale)</label>
                    <input
                      type="url"
                      value={storageFormData.public_endpoint}
                      onChange={(e) => setStorageFormData({ ...storageFormData, public_endpoint: e.target.value })}
                      placeholder="Se diverso: URL usato nei link scaricabili dal browser"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <button
                      type="button"
                      onClick={() => setIsEditingStorage(false)}
                      className="px-3 py-1.5 text-xs border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                    >
                      Annulla
                    </button>
                    <button
                      type="submit"
                      disabled={saveStorageConfig.isPending}
                      className="px-3 py-1.5 text-xs border border-blue-300 rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                    >
                      {saveStorageConfig.isPending ? 'Salvataggio...' : 'Salva'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'allegati' && isAdmin && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center">
              <PaperClipIcon className="h-6 w-6 text-gray-400 mr-3" />
              <div>
                <h2 className="text-lg font-medium text-gray-900">Allegati Promozioni</h2>
                <p className="text-sm text-gray-500">
                  Configura i formati di file ammessi per gli allegati delle promozioni del tenant.
                </p>
              </div>
            </div>
          </div>
          <div className="px-6 py-5 space-y-4">
            {tenantMeLoading ? (
              <div className="text-sm text-gray-500 py-3 text-center">Caricamento configurazione...</div>
            ) : (
              <>
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-2">Formati ammessi</h3>
                  <p className="text-xs text-gray-500 mb-3">
                    Solo i file con estensione e MIME type tra quelli selezionati potranno essere caricati come allegati.
                    Limiti hardcoded: max 20 allegati per promozione, max 25 MB per file.
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                    {ALL_KNOWN_FORMATS.map((fmt) => {
                      const checked = allegatiFormats.includes(fmt);
                      return (
                        <label
                          key={fmt}
                          className={`flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer text-sm transition-colors ${
                            checked
                              ? 'border-blue-500 bg-blue-50 text-blue-900'
                              : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              setAllegatiFormats((prev) =>
                                e.target.checked
                                  ? [...prev, fmt]
                                  : prev.filter((f) => f !== fmt)
                              );
                            }}
                            className="rounded text-blue-600"
                          />
                          <div className="min-w-0">
                            <div className="font-medium uppercase">.{fmt}</div>
                            <div className="text-xs text-gray-500 truncate">
                              {FORMAT_DESCRIPTIONS[fmt]}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {allegatiFormats.length === 0 && (
                  <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                    Seleziona almeno un formato. Una whitelist vuota disabilita di fatto gli upload.
                  </div>
                )}

                {allegatiDirty && removedFormats.length > 0 && (
                  <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                    Stai rimuovendo: <span className="font-medium">{removedFormats.map((f) => `.${f}`).join(', ')}</span>.
                    Gli allegati esistenti con queste estensioni restano scaricabili, ma non sarà più possibile caricarne di nuovi.
                  </div>
                )}

                <div className="flex items-center justify-between border-t border-gray-100 pt-4">
                  <p className="text-xs text-gray-500">
                    Le credenziali e i limiti tecnici sono gestiti dal sistema.
                  </p>
                  <div className="flex items-center gap-2">
                    {allegatiDirty && (
                      <button
                        type="button"
                        onClick={() => setAllegatiFormats(allegatiInitialFormats)}
                        disabled={saveAllegatiFormats.isPending}
                        className="px-3 py-1.5 text-xs border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                      >
                        Annulla
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => saveAllegatiFormats.mutate(allegatiFormats)}
                      disabled={
                        !allegatiDirty ||
                        allegatiFormats.length === 0 ||
                        saveAllegatiFormats.isPending
                      }
                      className="px-3 py-1.5 text-xs border border-blue-300 rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                    >
                      {saveAllegatiFormats.isPending ? 'Salvataggio...' : 'Salva'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Manual Mapping Modal */}
      {showMappingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full">
            <h3 className="text-lg font-semibold mb-4">Nuovo Mapping Prodotto</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prodotto</label>
                <select
                  value={selectedProdotto}
                  onChange={(e) => setSelectedProdotto(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="">Seleziona prodotto...</option>
                  {(productsForMapping || []).map((p: Prodotto) => (
                    <option key={p.id} value={p.id}>
                      {p.codice ? `[${p.codice}] ` : ''}{p.nome}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cerca Materiale WMS</label>
                <input
                  type="text"
                  value={materialSearch}
                  onChange={(e) => {
                    setMaterialSearch(e.target.value);
                    setSelectedMaterial(null);
                  }}
                  placeholder="Cerca per codice materiale..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
                {wmsMaterialResults && wmsMaterialResults.length > 0 && !selectedMaterial && (
                  <ul className="mt-1 border border-gray-200 rounded-lg max-h-40 overflow-auto">
                    {wmsMaterialResults.map((m: { id: string; code: string; name?: string }) => (
                      <li
                        key={m.id}
                        onClick={() => {
                          setSelectedMaterial(m);
                          setMaterialSearch(m.code);
                        }}
                        className="px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm"
                      >
                        <span className="font-mono">{m.code}</span>
                        {m.name && <span className="text-gray-500 ml-2">{m.name}</span>}
                      </li>
                    ))}
                  </ul>
                )}
                {selectedMaterial && (
                  <div className="mt-1 text-sm text-green-700 bg-green-50 px-3 py-1 rounded">
                    Selezionato: {selectedMaterial.code} {selectedMaterial.name && `- ${selectedMaterial.name}`}
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowMappingModal(false);
                  setSelectedMaterial(null);
                  setSelectedProdotto('');
                  setMaterialSearch('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
              >
                Annulla
              </button>
              <button
                onClick={() => {
                  if (!selectedProdotto || !selectedMaterial) {
                    toast.error('Seleziona sia un prodotto che un materiale WMS');
                    return;
                  }
                  createWmsMapping.mutate({
                    prodotto_id: selectedProdotto,
                    wms_material_id: selectedMaterial.id,
                    wms_material_code: selectedMaterial.code,
                    wms_material_name: selectedMaterial.name,
                  });
                }}
                disabled={!selectedProdotto || !selectedMaterial || createWmsMapping.isPending}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
              >
                {createWmsMapping.isPending ? 'Creazione...' : 'Crea Mapping'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Settings: React.FC = () => {
  const { user } = useAuth();
  return user?.is_host ? <HostSettings /> : <TenantSettings />;
};
export default Settings;
